import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { getAuthContext } from '../../utils/jwt';
import { addTrackingKey, TrackingApiKey } from '../../features/apicredentials/apiKeysSlice';
import { BASE_URL } from '../../utils/constants';

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface TrackingKey {
  id: string;
  tenantId: string;
  keyId: string;
  keyType: string;
  status: string;
  name: string;
  workspaceId: string;
  siteId: string;
  keyPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// Renders a modal for creating a new Track API key
const CreateTrackApiKeyModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [selectedKeyId, setSelectedKeyId] = useState('');
  const [workspaceOptions, setWorkspaceOptions] = useState<TrackingKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch tracking keys to get workspace options
  const fetchWorkspaceOptions = async () => {
    try {
      setLoading(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        setLoading(false);
        return;
      }


      const response = await fetch(
        `${BASE_URL}/api/settings/api-credentials/tracking-keys`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch tracking keys: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'OK' && Array.isArray(data.data)) {
        setWorkspaceOptions(data.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load workspace options');
      // console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch workspace options when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchWorkspaceOptions();
      setName('');
      setSelectedKeyId('');
    }
  }, [isOpen]);

  // Get selected tracking key data
  const getSelectedKey = (keyId: string): TrackingKey | undefined => {
    return workspaceOptions.find((key) => key.keyId === keyId);
  };

  // Get workspaceId and siteId for selected key
  const getWorkspaceData = (keyId: string) => {
    const selectedKey = getSelectedKey(keyId);
    return {
      workspaceId: selectedKey?.workspaceId || '',
      siteId: selectedKey?.siteId || '',
    };
  };

  // Generate idempotency key
  const generateIdempotencyKey = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
  };

  // Saves the new API key by calling the API
  const handleSave = async () => {
    if (!name || !selectedKeyId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        setSubmitting(false);
        return;
      }

      const idempotencyKey = generateIdempotencyKey();
      const { workspaceId, siteId } = getWorkspaceData(selectedKeyId);

      const requestBody = {
        name: name,
        workspaceId: workspaceId,
        siteId: siteId,
      };

      const response = await fetch(
        `${BASE_URL}/api/settings/api-credentials/tracking-keys`,
        {
          method: 'POST',
          headers: {
            'X-Idempotency-Key': idempotencyKey,
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create tracking key: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'ACCEPTED') {
        toast.success('API Key created successfully');

        // Create the new tracking key object
        const newTrackingKey: TrackingApiKey = {
          id: data.data?.id || `temp-${Date.now()}`,
          tenantId: tenantId,
          keyId: data.data?.keyId || `key-${Date.now()}`,
          keyType: 'TRACKING',
          status: 'ACTIVE',
          name: name,
          workspaceId: workspaceId,
          siteId: siteId,
          keyPrefix: data.data?.keyPrefix || name.substring(0, 6).toUpperCase(),
          createdAt: new Date().toISOString(),
          lastUsedAt: null,
        };

        // Add to Redux store immediately
        dispatch(addTrackingKey(newTrackingKey));

        onClose();
        // Reset form
        setName('');
        setSelectedKeyId('');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create API key');
      // console.error('Create error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Returns null if the modal is not open
  if (!isOpen) return null;

  // Determines if the Save button should be disabled
  const isSaveDisabled: boolean = !name || !selectedKeyId || submitting;

  // Get selected workspace data for display
  const selectedWorkspace = getSelectedKey(selectedKeyId);

  return (
    <div className='fixed inset-0 flex items-center justify-center bg-black/40 z-50'>
      <div className='bg-white rounded-lg shadow-lg w-[480px] p-6 relative'>
        {/* Header */}
        <div className='flex justify-between items-center mb-6'>
          <h2 className='text-[20px] font-semibold text-[#3F5265]'>
            Create Track API Key
          </h2>
          <button
            onClick={onClose}
            className='text-gray-500 hover:text-gray-700'
            disabled={submitting}
          >
            <FiXComponent size={20} />
          </button>
        </div>

        {/* Form */}
        <div className='space-y-5'>
          {/* Name */}
          <div>
            <label className='block text-[12px] text-[#3F5265] mb-1'>
              NAME <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              placeholder='Enter Name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              className='w-full border rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50'
            />
          </div>

          {/* Workspace */}
          <div>
            <label className='block text-[12px] text-[#3F5265] mb-1'>
              WORKSPACE <span className='text-red-500'>*</span>
            </label>
            <select
              value={selectedKeyId}
              onChange={(e) => setSelectedKeyId(e.target.value)}
              disabled={submitting || loading}
              className='w-full border rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50'
            >
              <option value=''>Select workspace</option>
              {loading ? (
                <option value='' disabled>Loading...</option>
              ) : (
                workspaceOptions.map((key) => (
                  <option key={key.keyId} value={key.keyId}>
                    {key.name} ({key.workspaceId})
                  </option>
                ))
              )}
            </select>
            {workspaceOptions.length === 0 && !loading && (
              <p className='text-xs text-gray-500 mt-1'>
                No workspaces available. Please create a tracking key first.
              </p>
            )}
            {selectedWorkspace && (
              <div className='mt-2 text-xs text-gray-500 space-y-1'>
                <p>Workspace ID: {selectedWorkspace.workspaceId}</p>
                <p>Site ID: {selectedWorkspace.siteId}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className='flex justify-end gap-3 mt-8'>
          <button
            onClick={onClose}
            disabled={submitting}
            className='px-5 py-2 rounded-lg border border-red-400 text-red-500 hover:bg-red-50 disabled:opacity-50'
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaveDisabled}
            className={`px-5 py-2 rounded-lg ${isSaveDisabled
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-orange-500 hover:bg-orange-600 text-white'
              }`}
          >
            {submitting ? 'Creating...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTrackApiKeyModal;